/**
 * BillingSuccess — landing page after Stripe redirects back. Polls the
 * checkout status until paid (max 5 attempts, 2s interval), then shows the
 * granted tier + expiration. Idempotent on the backend, so even parallel
 * polls won't double-grant.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('Missing session id in the URL.');
      return;
    }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        setResult(r.data);
        const ps = r.data?.payment_status;
        if (ps === 'paid') {
          setStatus('paid');
          return;
        }
        if (ps === 'unpaid' && r.data?.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Status check failed.');
        setStatus('error');
      }
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }} data-testid="billing-success-page">
      <div className="max-w-lg w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 size={28} className="text-[#2F8A92] mx-auto mb-4 animate-spin" />
            <div className="font-historical text-2xl md:text-3xl text-white">Confirming your pass…</div>
            <p className="font-mono text-xs md:text-sm text-white/55 mt-2">
              Polling Stripe — this usually takes a couple of seconds.
            </p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 size={36} className="text-emerald-300 mx-auto mb-4" />
            <div className="font-hud text-[10px] tracking-widest uppercase text-emerald-300 mb-2">Beta Pass active</div>
            <div className="font-historical text-3xl md:text-5xl text-[#D4A84A] leading-tight">Welcome in.</div>
            <p className="font-mono text-sm md:text-base text-white/75 mt-4">
              You're now <span className="text-[#D4A84A]">tier: {result?.tier || 'beta'}</span>.
              {result?.expires_at && (
                <> Pass expires {new Date(result.expires_at).toLocaleDateString()}.</>
              )}
            </p>
            <p className="font-mono text-[11px] text-white/45 mt-3">
              Test mode · transaction logged · live upon LLC verification.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="billing-success-continue-btn"
              className="mt-7 font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-3 hover:bg-[#D4A84A]/85 inline-flex items-center gap-2"
            >
              ENTER THE NETWORK <ArrowRight size={12} />
            </button>
          </>
        )}
        {status === 'expired' && (
          <>
            <AlertTriangle size={28} className="text-amber-300 mx-auto mb-4" />
            <div className="font-historical text-2xl md:text-3xl text-white">Session expired</div>
            <p className="font-mono text-sm text-white/65 mt-2">
              The checkout session timed out. No charge was made. Try again whenever you're ready.
            </p>
            <button
              onClick={() => navigate('/billing')}
              className="mt-5 font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-3 hover:bg-[#D4A84A]/85"
            >
              TRY AGAIN
            </button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <AlertTriangle size={28} className="text-amber-300 mx-auto mb-4" />
            <div className="font-historical text-2xl md:text-3xl text-white">Still processing…</div>
            <p className="font-mono text-sm text-white/65 mt-2">
              Stripe is taking longer than usual. Your pass will activate as soon as the
              payment confirms. You'll see it on your profile shortly.
            </p>
            <button
              onClick={() => navigate('/me')}
              className="mt-5 font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-3 hover:bg-[#D4A84A]/85"
            >
              GO TO MY PROFILE
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle size={28} className="text-red-300 mx-auto mb-4" />
            <div className="font-historical text-2xl md:text-3xl text-white">Couldn't verify the payment</div>
            <p className="font-mono text-sm text-white/65 mt-2">{error || 'Something went wrong.'}</p>
            <button
              onClick={() => navigate('/billing')}
              className="mt-5 font-hud text-xs tracking-widest text-white/85 border border-[#D4A84A]/40 px-5 py-3 hover:text-[#D4A84A]"
            >
              BACK TO BILLING
            </button>
          </>
        )}
      </div>
    </div>
  );
}
